/** @format */

import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import {
  RainbowKitProvider,
  getDefaultConfig,
  darkTheme,
} from "@rainbow-me/rainbowkit";
import { WagmiProvider, http, useAccount, useSwitchChain } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Link,
} from "react-router-dom";
import { useState, useEffect, CSSProperties } from "react";

import {
  WLCheckerPage,
  NFADisclaimer,
  StakingPage,
  PurrtardioPreview,
} from "./components";
import { hyperliquidEvmConfig } from "./constants";
import { Footer } from "./components/Footer";

// Configure wagmi and RainbowKit
const config = getDefaultConfig({
  appName: "Purrtardio",
  projectId: "d5c80eb2834ded4f0dadee9118514a95",
  chains: [hyperliquidEvmConfig],
  transports: {
    [hyperliquidEvmConfig.id]: http(),
  },
});

// Create a query client with error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

// Network switcher styles
const networkSwitcherStyles: Record<string, CSSProperties> = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#1a1a1a",
    padding: "2rem",
    borderRadius: "1rem",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
    textAlign: "center",
    maxWidth: "450px",
    width: "90%",
    border: "1px solid #ff69b4",
  },
  heading: {
    fontSize: "1.5rem",
    marginBottom: "1rem",
    color: "#ffffff",
  },
  text: {
    marginBottom: "1.5rem",
    color: "#dddddd",
    lineHeight: 1.5,
  },
  button: {
    backgroundColor: "#ff69b4",
    color: "#ffffff",
    border: "none",
    padding: "0.75rem 1.5rem",
    borderRadius: "2rem",
    fontSize: "1rem",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};

function NetworkSwitcher() {
  const { chainId } = useAccount();
  const { switchChain } = useSwitchChain();

  useEffect(() => {
    if (chainId && chainId !== hyperliquidEvmConfig.id) {
      // User is on the wrong network, prompt them to switch
      switchChain({ chainId: hyperliquidEvmConfig.id });
    }
  }, [chainId, switchChain]);

  if (chainId && chainId !== hyperliquidEvmConfig.id) {
    return (
      <div style={networkSwitcherStyles.overlay}>
        <div style={networkSwitcherStyles.modal}>
          <h2 style={networkSwitcherStyles.heading}>Wrong Network</h2>
          <p style={networkSwitcherStyles.text}>
            Please switch to {hyperliquidEvmConfig.name} to use Purrtardio.
          </p>
          <button
            onClick={() => switchChain({ chainId: hyperliquidEvmConfig.id })}
            style={networkSwitcherStyles.button}
          >
            Switch to {hyperliquidEvmConfig.name}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// Mobile Navigation Component
function MobileNavigation({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 left-0 h-full w-80 bg-black/95 backdrop-blur-md border-r border-purple-600/30 z-50 lg:hidden"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-purple-600/30">
                <div className="text-2xl font-bold text-white">PURRTARDIO</div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-pink-400 transition-colors p-2"
                  aria-label="Close menu"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="flex flex-col p-6 space-y-6">
                <Link
                  to="/"
                  onClick={onClose}
                  className="text-white hover:text-pink-400 transition-colors text-lg font-medium flex items-center space-x-3"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  <span>Home</span>
                </Link>

                {import.meta.env.VITE_SHOW_STAKE === "true" && (
                  <Link
                    to="/stake"
                    onClick={onClose}
                    className="text-white hover:text-pink-400 transition-colors text-lg font-medium flex items-center space-x-3"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"
                      />
                      <path d="M8 14v.5" />
                      <path d="M16 14v.5" />
                      <path d="M11.25 16.25h1.5L12 17l-.75-.75Z" />
                    </svg>
                    <span>Stake PURR</span>
                  </Link>
                )}

                <a
                  href="https://socks.purrtardio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="text-white hover:text-pink-400 transition-colors text-lg font-medium flex items-center space-x-3"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.6 20.4 9 21a3.38 3.38 0 1 1-4.9-4.9l3.5-3.5C8.4 11.6 9 10.4 9 9V3c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v10a5.15 5.15 0 0 1-1.5 3.6L15 21a3.38 3.38 0 1 1-4.9-4.9l3.5-3.5c.8-1 1.4-2.2 1.4-3.6V2"
                    />
                  </svg>
                  <span>Socks</span>
                  <svg
                    className="w-4 h-4 ml-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>

                <a
                  href="https://kingdom.purrtardio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="text-white hover:text-pink-400 transition-colors text-lg font-medium flex items-center space-x-3"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span>Kingdom</span>
                  <svg
                    className="w-4 h-4 ml-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </nav>

              {/* Footer in drawer */}
              <div className="mt-auto p-6 border-t border-purple-600/30">
                <p className="text-gray-400 text-sm">
                  © 2024 Purrtardio. All rights reserved.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function AppContent() {
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Ensure all resources are loaded before rendering
    setIsLoaded(true);

    // Cleanup function to prevent memory leaks
    return () => {
      // Cancel any pending operations or listeners
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  if (!isLoaded) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <NetworkSwitcher />

      {/* Responsive Navigation Bar */}
      <nav className="bg-black/80 backdrop-blur-sm border-b border-purple-600/30 p-4 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          {/* Logo */}
          <div className="text-xl font-bold text-white">PURRTARDIO</div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-6">
            <Link
              to="/"
              className="text-white hover:text-pink-400 transition-colors font-medium"
            >
              Home
            </Link>
            {import.meta.env.VITE_SHOW_STAKE === "true" && (
              <Link
                to="/stake"
                className="text-white hover:text-pink-400 transition-colors font-medium"
              >
                Stake PURR
              </Link>
            )}
            <a
              href="https://socks.purrtardio.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-pink-400 transition-colors font-medium flex items-center"
            >
              Socks
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-4 h-4 ml-1 inline-block"
              >
                <path
                  fillRule="evenodd"
                  d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17H4.25A2.25 2.25 0 012 14.75V6.25A2.25 2.25 0 014.25 4h4a.75.75 0 010 1.5h-4zm6.5-1.75a.75.75 0 000 1.5h1.636l-3.97 3.97a.75.75 0 101.06 1.06l3.97-3.97V8.25a.75.75 0 001.5 0V4.5a.75.75 0 00-.75-.75h-3.5z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://kingdom.purrtardio.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-pink-400 transition-colors font-medium flex items-center"
            >
              Kingdom
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-4 h-4 ml-1 inline-block"
              >
                <path
                  fillRule="evenodd"
                  d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17H4.25A2.25 2.25 0 012 14.75V6.25A2.25 2.25 0 014.25 4h4a.75.75 0 010 1.5h-4zm6.5-1.75a.75.75 0 000 1.5h1.636l-3.97 3.97a.75.75 0 101.06 1.06l3.97-3.97V8.25a.75.75 0 001.5 0V4.5a.75.75 0 00-.75-.75h-3.5z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden text-white hover:text-pink-400 transition-colors p-2"
            aria-label="Open menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      <MobileNavigation
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PurrtardioPreview />} />
          {import.meta.env.VITE_SHOW_STAKE === "true" && (
            <Route path="/stake" element={<StakingPage />} />
          )}
        </Routes>
      </AnimatePresence>

      {/* Footer Component */}
      <Footer />
    </>
  );
}

function App() {
  // Error boundary to catch and handle errors
  // const [hasError, setHasError] = useState(false);

  // useEffect(() => {
  //   const handleError = (event: ErrorEvent) => {
  //     console.error("Global error:", event.error);
  //     setHasError(true);
  //   };

  //   window.addEventListener("error", handleError);

  //   return () => {
  //     window.removeEventListener("error", handleError);
  //   };
  // }, []);

  // if (hasError) {
  //   return (
  //     <div className="error-fallback">
  //       Something went wrong. Please refresh the page.
  //     </div>
  //   );
  // }

  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={darkTheme({
            accentColor: "#ff69b4",
            accentColorForeground: "#ffffff",
            borderRadius: "large",
          })}
        >
          <Router>
            <NFADisclaimer />
            <AppContent />
            <ToastContainer />
          </Router>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;
